import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Globe, Plus, CheckCircle2, Edit2, Save, X, Sparkles, AlertCircle } from 'lucide-react';
import { DomainStatus } from '@ftt/shared';

export const AdminDomainsPage: React.FC = () => {
  const [editingDomain, setEditingDomain] = useState<any>(null);
  const [domainName, setDomainName] = useState('');
  const [status, setStatus] = useState<DomainStatus>('UNDER_EVALUATION');
  const [registrar, setRegistrar] = useState('');
  const [notes, setNotes] = useState('');
  const [isPreferred, setIsPreferred] = useState(false);

  const queryClient = useQueryClient();

  const { data: domainsRes, isLoading } = useQuery({
    queryKey: ['admin-domains'],
    queryFn: () => api.get('/admin/domains').then((res) => res.data.data),
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      return api.post('/admin/domains', {
        id: editingDomain?.id || undefined,
        domainName,
        status,
        registrar,
        notes,
        isPreferred,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-domains'] });
      setEditingDomain(null);
    },
  });

  const domains = domainsRes || [];

  const openEditor = (dom?: any) => {
    if (dom) {
      setEditingDomain(dom);
      setDomainName(dom.domainName);
      setStatus(dom.status);
      setRegistrar(dom.registrar || '');
      setNotes(dom.notes || '');
      setIsPreferred(dom.isPreferred);
    } else {
      setEditingDomain({ id: '' });
      setDomainName('');
      setStatus('UNDER_EVALUATION');
      setRegistrar('Hostinger');
      setNotes('');
      setIsPreferred(false);
    }
  };

  return (
    <div className="space-y-6 font-sans text-slate-900 antialiased">
      
      {/* 1. Header with High Visibility */}
      <div className="bg-white rounded-2xl md:rounded-3xl p-6 sm:p-8 shadow-sm border border-slate-200/90 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-brand-700 font-mono uppercase tracking-widest font-bold">
              INFRASTRUCTURE & HOSTING
            </span>
            <span className="text-slate-400 font-mono text-xs">&bull;</span>
            <span className="text-xs text-slate-600 font-serif italic">
              FOR THE TRUTH PRESS PRIVATE LIMITED
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-serif font-black text-slate-900 flex items-center gap-2.5 mt-1">
            <Globe className="w-7 h-7 text-[#FF6B4A]" />
            <span>Domain Name Planning &amp; Evaluation</span>
          </h1>
          <p className="text-xs sm:text-sm text-slate-600 font-medium mt-1">
            Internal evaluation matrix for domain naming strategies, brand acquisition candidates, and registrar notes.
          </p>
        </div>

        <button
          onClick={() => openEditor()}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-extrabold text-xs shadow-md transition-all self-start sm:self-auto cursor-pointer"
        >
          <Plus className="w-4 h-4 text-[#FF6B4A]" />
          <span>Add Domain Candidate</span>
        </button>
      </div>

      {/* 2. Notice */}
      <div className="p-4 rounded-2xl bg-white border border-slate-200/90 text-xs text-slate-600 space-y-1 shadow-sm">
        <div className="flex items-center gap-2 text-brand-700 font-bold uppercase font-mono text-[10px]">
          <Sparkles className="w-3.5 h-3.5 text-[#FF6B4A]" />
          <span>Internal Planning Only</span>
        </div>
        <p>
          This module is an internal research repository for assessing candidate domains before purchasing via Hostinger or approved registrars. It is never displayed publicly.
        </p>
      </div>

      {/* 3. Domain Cards Grid */}
      {isLoading ? (
        <div className="p-16 text-center text-xs font-mono text-slate-500 bg-white rounded-2xl border border-slate-200 shadow-sm">Loading domain candidates...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {domains.map((dom: any) => (
            <div
              key={dom.id}
              className={`p-6 rounded-2xl md:rounded-3xl bg-white border transition-all space-y-4 flex flex-col justify-between shadow-sm hover:shadow-md ${
                dom.isPreferred ? 'border-brand-500 ring-2 ring-brand-500/20' : 'border-slate-200/90'
              }`}
            >
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className={`text-[10px] font-mono font-bold uppercase px-2.5 py-1 rounded-lg ${
                    dom.status === 'RECOMMENDED'
                      ? 'bg-brand-50 text-brand-700 border border-brand-200'
                      : dom.status === 'AVAILABLE'
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : 'bg-slate-100 text-slate-700 border border-slate-200'
                  }`}>
                    {dom.status.replace('_', ' ')}
                  </span>
                  {dom.isPreferred && (
                    <span className="text-[10px] font-mono uppercase font-bold text-[#FF6B4A] flex items-center gap-1">
                      ★ Preferred Choice
                    </span>
                  )}
                </div>

                <h3 className="font-mono text-lg font-bold text-slate-900 tracking-wider">
                  {dom.domainName}
                </h3>

                <div className="text-xs text-slate-600 space-y-1">
                  <div>Registrar: <strong className="text-slate-900">{dom.registrar || 'Hostinger'}</strong></div>
                  {dom.notes && <p className="text-slate-700 italic pt-1">&ldquo;{dom.notes}&rdquo;</p>}
                </div>
              </div>

              <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs">
                <span className="text-[10px] font-mono text-slate-500">
                  Checked: {dom.checkedDate ? new Date(dom.checkedDate).toLocaleDateString() : 'Pending'}
                </span>
                <button
                  onClick={() => openEditor(dom)}
                  className="px-3.5 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold inline-flex items-center gap-1.5 transition-colors cursor-pointer"
                >
                  <Edit2 className="w-3.5 h-3.5 text-[#FF6B4A]" />
                  <span>Update</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 4. Modal */}
      {editingDomain && (
        <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-8 space-y-6 shadow-2xl border border-slate-200">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <span className="text-[10px] font-mono uppercase tracking-widest text-brand-700 font-bold">DOMAIN REGISTRATION</span>
                <h3 className="font-serif text-xl font-bold text-slate-900">
                  {editingDomain.id ? 'Update Domain Candidate' : 'Add Domain Candidate'}
                </h3>
              </div>
              <button 
                onClick={() => setEditingDomain(null)} 
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-4 text-xs font-sans">
              <div>
                <label className="block font-bold text-slate-700 uppercase font-mono mb-1">
                  Domain Name *
                </label>
                <input
                  type="text"
                  value={domainName}
                  onChange={(e) => setDomainName(e.target.value)}
                  placeholder="e.g. forthetruthpublishing.com"
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-mono focus:outline-hidden focus:border-brand-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 uppercase font-mono mb-1">Evaluation Status</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value as any)}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 focus:outline-hidden focus:border-brand-500"
                  >
                    <option value="UNDER_EVALUATION">UNDER_EVALUATION</option>
                    <option value="AVAILABLE">AVAILABLE</option>
                    <option value="RECOMMENDED">RECOMMENDED</option>
                    <option value="TAKEN">TAKEN</option>
                    <option value="PURCHASED">PURCHASED</option>
                  </select>
                </div>

                <div>
                  <label className="block font-bold text-slate-700 uppercase font-mono mb-1">Registrar</label>
                  <input
                    type="text"
                    value={registrar}
                    onChange={(e) => setRegistrar(e.target.value)}
                    placeholder="Hostinger / Namecheap"
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 focus:outline-hidden focus:border-brand-500"
                  />
                </div>
              </div>

              <div>
                <label className="block font-bold text-slate-700 uppercase font-mono mb-1">Internal Notes &amp; Rationale</label>
                <textarea
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Strategic purpose, keywords, price, or branding notes..."
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 focus:outline-hidden focus:border-brand-500"
                />
              </div>

              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  id="pref"
                  checked={isPreferred}
                  onChange={(e) => setIsPreferred(e.target.checked)}
                  className="w-4 h-4 rounded bg-white border-slate-300 text-brand-600 focus:ring-brand-500"
                />
                <label htmlFor="pref" className="text-xs text-slate-700 select-none font-medium">
                  Mark as Preferred Candidate for Publishing Website
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
              <button
                onClick={() => setEditingDomain(null)}
                className="px-4 py-2 rounded-xl text-slate-600 hover:bg-slate-100 font-bold transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={() => saveMutation.mutate()}
                disabled={!domainName || saveMutation.isPending}
                className="px-5 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs shadow-md transition-colors cursor-pointer disabled:opacity-50"
              >
                Save Domain Candidate
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
